import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Button from '@components/button';
import AdvancedSearch from '@components/advanced-search';
import Filters from '@components/filters';
import Modal from '@components/modal';

import { updateSingleFilter } from '@src/store/slices/searchFiltersSlice';

// ---------------------------------------------------------

import {
  tag_container,
  tag,
  add_location_btn,
  partial_search_text,
  results_filters_container,
  results_headings_container,
  new_search_button,
  save_search_button
} from './styles.module.scss';
import InputField from '@components/inputs/input/component';
import { pushStateToQuery } from '@lib/query-utils';

// ---------------------------------------------------------

const ResultsPageFilter = (props) => {
  let { headerValue, selections, setPageNum, handleSaveSearch } = props;

  const { user } = useUser();
  const dispatch = useDispatch();
  const router = useRouter();

  const reduxParams = useSelector((state) => state.searchFilters);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [saveSearchModalIsOpen, setSaveSearchModalIsOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [isNewSearch, setIsNewSearch] = useState(false);

  const handleRemoveOption = (option) => {
    const newLocations = reduxParams.locations.filter((el, idx) => idx !== option);
    dispatch(updateSingleFilter({ group: 'locations', value: newLocations }));
    pushStateToQuery(router, { ...reduxParams, locations: newLocations });
  };

  const handlePillsKeyDown = (e, option) => {
    if (e.key === 'Enter') handleRemoveOption(option);
  };

  // ---------------------------------------------------------

  const handleOpenModal = () => {
    setModalIsOpen(true);
    setIsNewSearch(false);
  };

  const handleNewSearchModal = () => {
    setModalIsOpen(true);
    setIsNewSearch(true);
  };

  const handleSaveSearchClick = () => {
    if (user) return setSaveSearchModalIsOpen(true);
    if (typeof window !== 'undefined') {
      window.location.href = '/api/auth/login';
    } else {
      router.push('/api/auth/login');
    }
  };

  const handleSaveSearchSubmit = () => {
    handleSaveSearch(saveSearchName);
    setSaveSearchModalIsOpen(false);
  };

  const newSearchButton = (
    <Button
      label="New search"
      className={new_search_button}
      theme="text-only"
      type="button"
      onClick={() => handleNewSearchModal()}
    />
  );

  return (
    <div>
      <div className={results_headings_container}>
        <h1>{headerValue}</h1>
        {newSearchButton}
      </div>

      <div className={tag_container}>
        {router.query.address && (
          <span className={partial_search_text}>Partial Street Search: {router.query.address}</span>
        )}
        {!router.query.address &&
          (reduxParams.locations || []).map((el, idx) => (
            <button
              className={tag}
              key={el.label}
              tabIndex={0}
              onKeyDown={(e) => handlePillsKeyDown(e, idx)}
              onClick={() => handleRemoveOption(idx)}
              style={{ backgroundImage: 'url("/images/icons/icons-actions-close.svg")' }}
            >
              {el.label}
            </button>
          ))}
        {!router.query.address && (
          <Button
            className={add_location_btn}
            label="Add location or school district"
            theme="text-only"
            type="button"
            onClick={() => handleOpenModal()}
          />
        )}
      </div>

      <div className={results_filters_container}>
        <Filters />
        <div>
          {newSearchButton}
          <Button
            label="Save search"
            theme="text-only"
            type="button"
            onClick={handleSaveSearchClick}
          />
        </div>
      </div>

      {modalIsOpen && (
        <AdvancedSearch
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          isNewSearch={isNewSearch}
          existingValues={selections}
          setPageNum={setPageNum}
        />
      )}

      <Modal
        modalIsOpen={saveSearchModalIsOpen}
        onModalClose={() => setSaveSearchModalIsOpen(false)}
      >
        <h2>Saving Search</h2>
        <InputField
          required
          label="Enter a name to find your search later"
          placeholder="My new search..."
          searchValueCallback={setSaveSearchName}
        />
        <Button
          className={save_search_button}
          label="Save Search"
          title="Save Search"
          onClick={handleSaveSearchSubmit}
          disabled={!saveSearchName}
        />
      </Modal>
    </div>
  );
};

ResultsPageFilter.propTypes = {
  filterChangeCallback: PropTypes.func,

  /**
   * Function to handle saving of searches to user account
   */
  handleSaveSearch: PropTypes.func,

  /**
   * Specifies the header to be shown on the page, based on filter selections
   */
  headerValue: PropTypes.string,

  /**
   * Specifies the locations selected in the search
   */
  selections: PropTypes.any,

  /**
   * A callback function that specifies the locations selected in the search
   */
  selectionsCallback: PropTypes.func,

  /**
   * Set the page num on the results page
   */
  setPageNum: PropTypes.func
};

ResultsPageFilter.defaultProps = {};

export default ResultsPageFilter;
